import "maplibre-gl/dist/maplibre-gl.css";

import { inject, injectable } from "inversify";
import maplibregl, { type LayerSpecification, setMaxParallelImageRequests, setWorkerCount } from "maplibre-gl";
import { DomElementsService, type IService, Logger, PALETTE, type XYZ } from "@designboticteam/core";
import { MapConfigService, MapStoreService } from "@designboticteam/map";

type MapLayer = Exclude<LayerSpecification, { type: "background" }>;

const BUILDINGS_LAYER_ID = "3d-buildings" as const;

@injectable()
class MapLibreInstanceService implements IService {
	public readonly serviceName = "MapLibreInstanceService";
	private logger = new Logger(`🌍 MAP | ✏️ ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#00aa00" });

	#map: maplibregl.Map | null = null;
	public get map(): maplibregl.Map {
		if (this.#map === null) {
			throw new Error("MapLibreInstanceService not initialized");
		}

		return this.#map;
	}

	constructor(
		@inject(DomElementsService) private domElementsService: DomElementsService,
		@inject(MapConfigService) private mapConfigService: MapConfigService,
		@inject(MapStoreService) private mapStoreService: MapStoreService,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		this.#map = new maplibregl.Map({
			container: this.domElementsService.containerElement,
			style: this.mapConfigService.style,
			center: this.mapConfigService.origin,
			zoom: this.mapConfigService.zoom,
			hash: this.mapConfigService.hash,
			attributionControl: false,
			fadeDuration: 0,
			cancelPendingTileRequestsWhileZooming: true,
			canvasContextAttributes: {
				antialias: true,
				powerPreference: "high-performance",
			},
			minPitch: 0,
			maxPitch: 80,
		});

		setWorkerCount(4);

		setMaxParallelImageRequests(4);
		this.map.getCanvas().style.cursor = "default";
		this.map.on("dblclick", (event) => event.preventDefault());
		this.map.on("load", () => this.addBuildingsLayer());
		this.map.on("idle", () => this.domElementsService.containerElement.dispatchEvent(new CustomEvent("__e2e__:map:idle", { bubbles: true })));

		this.logger.log("initialized");
	}

	public dispose(): void {
		this.map.style._load = () => {};

		const positionHash = window.location.hash;

		this.map.remove();

		if (this.mapConfigService.hash === true && positionHash !== "") {
			history.replaceState(history.state, "", positionHash);
		}

		this.#map = null;

		this.logger.log("disposed");
	}

	/**
	 * Maplibre layers with `background` layer for better types experience (background is solid color/pattern without source)
	 */
	public getLayers() {
		const mapStyle = this.map.getStyle(); // Types are incorrect, it could be undefined

		if (mapStyle === undefined) {
			return [];
		}

		return mapStyle.layers.filter((l): l is MapLayer => l.type !== "background");
	}

	/** */
	public removeSource({ sourceId }: { sourceId: string }): void {
		const mapLayers = this.getLayers();

		for (const layer of mapLayers) {
			if (layer.source === sourceId) {
				this.map.removeLayer(layer.id);
			}
		}

		if (this.map.getSource(sourceId)) {
			this.map.removeSource(sourceId);
		}
	}

	/** */
	public async captureScreenshot({ quality = 0.7 }: { quality?: number } = {}): Promise<string> {
		const map = this.map;

		return new Promise((resolve) => {
			map.once("render", () => {
				const dataUrl = map.getCanvas().toDataURL("image/jpeg", quality);
				resolve(dataUrl);
			});
			map.triggerRepaint();
		});
	}

	/** */
	public captureMapState(): {
		center: [number, number];
		zoom: number;
		canvasSize: { width: number; height: number };
	} {
		const map = this.map;
		const center = map.getCenter();
		const canvas = map.getCanvas();
		return {
			center: [center.lng, center.lat],
			zoom: map.getZoom(),
			canvasSize: { width: canvas.clientWidth, height: canvas.clientHeight },
		};
	}

	/** */
	public setSourceVisibility({ sourceId, isVisible }: { sourceId: string; isVisible: boolean }): void {
		const visibility = isVisible ? "visible" : "none";

		for (const mapLayer of this.getLayers()) {
			if (mapLayer.source !== sourceId) {
				continue;
			}

			this.map.setLayoutProperty(mapLayer.id, "visibility", visibility);
		}
	}

	/** */
	private addBuildingsLayer(): void {
		const candidates = ["maptiler_planet", "openmaptiles", "composite"];
		const source = candidates.find((s) => this.map.getSource(s));
		if (!source) {
			this.logger.log("No known vector source found for 3D buildings");
			return;
		}

		this.map.addLayer({
			id: BUILDINGS_LAYER_ID,
			source,
			"source-layer": "building",
			type: "fill-extrusion",
			minzoom: 15,
			// The layer is created asynchronously on map "load", so the stored flag is the source of truth.
			layout: { visibility: this.mapStoreService.state.layers.buildings3d ? "visible" : "none" },
			paint: {
				"fill-extrusion-color": PALETTE.accent.graphite,
				"fill-extrusion-height": ["coalesce", ["get", "render_height"], ["get", "height"], 5],
				"fill-extrusion-base": ["coalesce", ["get", "render_min_height"], ["get", "min_height"], 0],
				"fill-extrusion-opacity": 0.5,
			},
		});
	}

	/**
	 * Show/hide the 3D buildings layer.
	 */
	public setBuildingsVisible(isVisible: boolean): void {
		this.mapStoreService.setState((state) => ({ layers: { ...state.layers, buildings3d: isVisible } }));

		if (this.map.getLayer(BUILDINGS_LAYER_ID) !== undefined) {
			this.map.setLayoutProperty(BUILDINGS_LAYER_ID, "visibility", isVisible ? "visible" : "none");
		}
	}

	/**
	 * Maplibre earth radius != Web Mercator radius
	 * ~6_371_008.8
	 */
	public getMapLibreEarthRadius(): number {
		return this.getMapLibreEarthCircumference() / (2 * Math.PI);
	}

	/**
	 * ~40_030_173.59
	 */
	public getMapLibreEarthCircumference(): number {
		const equator = maplibregl.MercatorCoordinate.fromLngLat([0, 0], 0);
		return 1 / equator.meterInMercatorCoordinateUnits();
	}

	// `scene` = metres relative to the map origin, mercator Y (+south) flipped to Z (+north)
	public wgs84ToScene(lngLat: maplibregl.LngLatLike): XYZ {
		const point = maplibregl.MercatorCoordinate.fromLngLat(lngLat);
		const origin = maplibregl.MercatorCoordinate.fromLngLat(this.mapConfigService.origin);
		const m = origin.meterInMercatorCoordinateUnits();

		return [(point.x - origin.x) / m, 0, -(point.y - origin.y) / m];
	}

	// inverse of `wgs84ToScene`
	public sceneToWgs84({ x, y = 0, z }: { x: number; y?: number; z: number }): [number, number] {
		const origin = maplibregl.MercatorCoordinate.fromLngLat(this.mapConfigService.origin);
		const m = origin.meterInMercatorCoordinateUnits();

		const mercator = new maplibregl.MercatorCoordinate(origin.x + x * m, origin.y - z * m, origin.z + y * m);

		const lngLat = mercator.toLngLat();
		return [lngLat.lng, lngLat.lat];
	}
}

export { MapLibreInstanceService };
